import React from "react";
import { ArrowUpIcon, ArrowDownIcon } from "../icons";

/**
 * Card component with enhanced styling, animations, and variants
 * Includes subcomponents for header, content, footer, etc.
 *
 * @param {Object} props
 * @param {React.ReactNode} props.children - Card content
 * @param {string} props.className - Additional class names
 * @param {string} props.variant - Card style variant (default, elevated, outline, glassy)
 * @param {boolean} props.hoverable - Whether to show hover effects
 */
const Card = React.memo(
  ({
    children,
    className = "",
    variant = "default",
    hoverable = false,
    highlightOnHover = false,
    decorative = false,
    accent = false,
    as: Component = "div",
    loading = false,
    // Elevation (DESIGN.md "Glass and depth"): 1 rests on the canvas (the
    // default), 2 is raised (the one focal card). Hoverable cards rise to 2.
    elevation,
    // Accepted for compatibility; Signal panels have no glow.
    // eslint-disable-next-line no-unused-vars
    glow = false,
    // eslint-disable-next-line no-unused-vars
    glowColor = "primary",
    ...props
  }) => {
    // Every variant is the same Signal panel; amber/red variants tint the hairline only.
    const panel = "sg-card";
    const variantClasses = {
      minimal: "bg-transparent border-0 rounded-xl",
    };
    const variantTone = {
      "gradient-amber": "warn",
      "gradient-red": "danger",
    };

    // Enhanced hover effects
    const hoverClasses = hoverable ? "group" : "";

    // Loading shimmer overlay
    const loadingOverlay = loading && (
      <div className="absolute inset-0 overflow-hidden rounded-xl pointer-events-none z-20">
        <div className="sg-skeleton absolute inset-0 opacity-40" />
      </div>
    );

    // Decorative accent tick (hairline-weight, ink only)
    const decorativeElements = decorative && (
      <div
        className="absolute top-0 right-0 w-1 h-8 rounded-bl-md bg-sg-line-strong"
        aria-hidden="true"
      ></div>
    );

    // Accent option: a stronger hairline
    const accentBorder = accent ? "!border-sg-line-strong" : "";

    return (
      <Component
        className={`${variantClasses[variant] || panel} ${hoverClasses} ${accentBorder} ${loading ? "relative" : ""} ${className}`}
        data-tone={variantTone[variant]}
        data-interactive={hoverable ? "true" : undefined}
        data-elev={elevation}
        {...props}
      >
        {decorativeElements}
        {loadingOverlay}
        <div className="relative z-10">{children}</div>
        {/* no glass overlay needed in clean design */}
      </Component>
    );
  },
);

Card.displayName = "Card";

/**
 * CardHeader component for the title section of a card
 */
export const CardHeader = React.memo(
  ({
    className = "",
    children,
    withGradient = false,
    withImage = false,
    compact = false,
    divider = true,
    ...props
  }) => {
    const dividerClass = divider
      ? "border-b border-sg-line"
      : "";
    const paddingClass = compact ? "px-4 py-3" : "px-6 py-4";
    const gradientClass = withGradient
      ? "bg-sg-fill"
      : "";
    const imageClass = withImage ? "pb-10" : "";

    return (
      <div
        className={`${paddingClass} ${dividerClass} ${gradientClass} ${imageClass} ${className}`}
        {...props}
      >
        {children}
      </div>
    );
  },
);

CardHeader.displayName = "CardHeader";

/**
 * CardTitle component for card titles with enhanced typography and animations
 */
export const CardTitle = React.memo(
  ({
    className = "",
    children,
    as: Component = "h3",
    gradient = false,
    badge,
    icon,
    ...props
  }) => {
    // `gradient` is accepted for compatibility; titles stay solid ink.
    const gradientClass = gradient ? "text-sg-ink" : "text-sg-ink";

    return (
      <div className="flex items-center gap-2">
        {icon && <div className="inline-flex mr-2 text-sg-ink-2">{icon}</div>}
        <Component
          className={`text-[15px] font-semibold leading-tight ${gradientClass} ${className}`}
          {...props}
        >
          {children}
        </Component>
        {badge && <div className="ml-2">{badge}</div>}
      </div>
    );
  },
);

CardTitle.displayName = "CardTitle";

/**
 * CardSubtitle component for card subtitles with enhanced styling
 */
/**
 * CardDescription component for card descriptions with enhanced styling
 */
export const CardDescription = React.memo(
  ({ className = "", children, muted = true, withIcon, ...props }) => {
    return (
      <p
        className={`mt-1 text-sm ${muted ? "text-sg-ink-3" : "text-sg-ink-2"} ${className}`}
        {...props}
      >
        {withIcon && (
          <span className="inline-flex items-center mr-1.5 text-sg-ink-3">
            {withIcon}
          </span>
        )}
        {children}
      </p>
    );
  },
);

CardDescription.displayName = "CardDescription";

export const CardSubtitle = React.memo(
  ({ className = "", children, muted = false, withIcon, ...props }) => {
    return (
      <p
        className={`mt-2 text-sm ${muted ? "text-sg-ink-2" : "text-sg-ink"} ${className}`}
        {...props}
      >
        {withIcon && (
          <span className="inline-flex items-center mr-1.5 text-sg-ink-3">
            {withIcon}
          </span>
        )}
        {children}
      </p>
    );
  },
);

CardSubtitle.displayName = "CardSubtitle";

/**
 * CardContent component for the main content area of a card with enhanced padding options
 */
export const CardContent = React.memo(
  ({
    className = "",
    children,
    padding = "default",
    withDividers = false,
    ...props
  }) => {
    const paddingClasses = {
      none: "p-0",
      small: "p-3",
      default: "p-6",
      large: "p-8",
    };

    const dividerClass = withDividers
      ? "divide-y divide-sg-line"
      : "";

    return (
      <div
        className={`${paddingClasses[padding]} ${dividerClass} ${className}`}
        {...props}
      >
        {children}
      </div>
    );
  },
);

CardContent.displayName = "CardContent";

/**
 * CardFooter component for the footer section of a card with enhanced styling
 */
export const CardFooter = React.memo(
  ({
    className = "",
    children,
    variant = "default",
    actions = false,
    sticky = false,
    ...props
  }) => {
    const variantClasses = {
      default: "border-t border-sg-line",
      subtle: "bg-sg-fill border-t border-sg-line",
      none: "",
      primary: "border-t border-sg-line-strong",
    };

    const actionClass = actions
      ? "flex justify-end items-center space-x-2"
      : "";
    const stickyClass = sticky ? "sticky bottom-0 z-10" : "";

    return (
      <div
        className={`px-6 py-4 ${variantClasses[variant]} ${actionClass} ${stickyClass} ${className}`}
        {...props}
      >
        {children}
      </div>
    );
  },
);

CardFooter.displayName = "CardFooter";

/**
 * CardMedia component for image or video content in cards with enhanced effects
 */
export const CardMedia = React.memo(
  ({
    src,
    alt = "",
    aspectRatio = "16/9",
    overlay,
    className = "",
    rounded = false,
    zoom = false,
    blur = false,
    ...props
  }) => {
    const roundedClass = rounded ? "rounded-lg overflow-hidden" : "";
    const zoomClass = zoom
      ? "motion-safe:group-hover:scale-[1.02] motion-safe:transition-transform motion-safe:duration-[240ms]"
      : "";
    const blurClass = blur ? "blur-sm" : "";

    return (
      <div
        className={`relative overflow-hidden ${roundedClass} ${className}`}
        style={{ aspectRatio }}
        {...props}
      >
        <img
          src={src}
          alt={alt}
          loading="lazy"
          className={`w-full h-full object-cover ${zoomClass} ${blurClass}`}
        />
        {overlay && (
          <div className="absolute inset-0 bg-[var(--sg-scrim)] flex items-end">
            <div className="p-5 text-white w-full">{overlay}</div>
          </div>
        )}
      </div>
    );
  },
);

CardMedia.displayName = "CardMedia";

/**
 * CardActionArea component for clickable card areas with enhanced interactions
 */
export const CardActionArea = React.memo(
  ({
    className = "",
    children,
    onClick,
    active = false,
    withRipple = true,
    rounded = true,
    withIcon,
    ...props
  }) => {
    return (
      <button
        className={`
        w-full text-left block relative overflow-hidden
        transition-colors duration-[120ms]
        ${rounded ? "rounded-xl" : ""}
        ${
          active
            ? "bg-sg-fill-active text-sg-ink"
            : "hover:bg-sg-fill-hover text-sg-ink"
        }
        focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--sg-focus)]
        ${className}
      `}
        onClick={onClick}
        role="button"
        {...props}
      >
        <div className="flex items-center justify-between p-4">
          <div className="flex items-center">
            {withIcon && <div className="mr-3 text-sg-ink-2">{withIcon}</div>}
            <div>{children}</div>
          </div>
          <div className="text-sg-ink-3">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-5 w-5"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 5l7 7-7 7"
              />
            </svg>
          </div>
        </div>
      </button>
    );
  },
);

CardActionArea.displayName = "CardActionArea";

/**
 * CardBadge component for displaying badge-like elements in cards
 */
export const CardBadge = React.memo(
  ({
    className = "",
    children,
    variant = "default",
    size = "md",
    ...props
  }) => {
    const tone =
      { success: "live", warning: "warn", danger: "danger", info: "info" }[
        variant
      ] || "neutral";

    return (
      <span
        className={`sg-badge ${className}`}
        data-tone={tone}
        data-size={size === "lg" ? "lg" : undefined}
        {...props}
      >
        {children}
      </span>
    );
  },
);

CardBadge.displayName = "CardBadge";

/**
 * CardStat component for displaying key metrics or statistics in cards
 * Enhanced with gradient backgrounds, shadows, and animations to match
 * the performance metrics card styling.
 */
export const CardStat = React.memo(
  ({
    className = "",
    label,
    value,
    icon,
    trend,
    trendValue,
    variant = "default",
    showProgress = false,
    progressValue = 0,
    ...props
  }) => {
    // Colour is information: only success/warning/danger tint the number.
    const valueTone = {
      success: "text-sg-live",
      warning: "text-sg-warn",
      danger: "text-sg-danger",
    };
    const barTone = {
      success: "bg-sg-live",
      warning: "bg-sg-warn",
      danger: "bg-sg-danger",
    };

    const trendClasses = {
      up: "text-sg-live",
      down: "text-sg-danger",
      neutral: "text-sg-ink-3",
    };

    return (
      <div
        className={`sg-card overflow-hidden ${className}`}
        {...props}
      >
        <div className="p-5">
          <div className="flex justify-between items-start mb-2">
            {label && (
              <div className="sg-micro">
                {label}
              </div>
            )}
            {icon && <div className="text-sg-ink-3">{icon}</div>}
          </div>

          <div className="flex items-baseline">
            <div
              className={`font-sans text-[28px] font-semibold leading-none tracking-[-0.02em] ${valueTone[variant] || "text-sg-ink"}`}
            >
              {value}
            </div>
            {trend && trendValue && (
              <div
                className={`ml-2 font-mono text-xs flex items-center ${trendClasses[trend]}`}
              >
                {trend === "up" && <ArrowUpIcon className="h-4 w-4 mr-0.5" />}
                {trend === "down" && (
                  <ArrowDownIcon className="h-4 w-4 mr-0.5" />
                )}
                {trendValue}
              </div>
            )}
          </div>

          {/* Optional progress bar */}
          {showProgress && (
            <div className="mt-4">
              <div className="flex items-center justify-between font-mono text-[11px] text-sg-ink-3">
                <span>Progress</span>
                <span>{progressValue}%</span>
              </div>
              <div className="mt-2 h-1.5 bg-sg-fill-active rounded-full overflow-hidden">
                <div
                  className={`h-full rounded-full ${barTone[variant] || "bg-sg-ink"}`}
                  style={{ width: `${progressValue}%` }}
                ></div>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  },
);

CardStat.displayName = "CardStat";

// Export Card as both default and named export to fix import issues
export { Card };
export default Card;
